import { EntityUtils } from '@record-it-npm/synchro-client';
import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { PageContentContainer } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useAppDispatch } from '../../../store/hooks';
import { createWorkTimeEntrySlice } from '../../../store/slice/createWorkTimeEntry/createWorkTimeEntrySlice';
import {
  TimeTrackingActivityToSelectListItem,
  TimeTrackingActivityToSelectListItemProps
} from '../../timeTracking/TimeTrackingActivityToSelectListItem/TimeTrackingActivityToSelectListItem';
import { useConfigureCreateWorkTimeEntrySelectActivityPageBreadcrumbs } from './useConfigureCreateWorkTimeEntrySelectActivityPageBreadcrumbs';
import { useCreateWorkTimeEntrySelectActivityPageActivities } from './useCreateWorkTimeEntrySelectActivityPageActivities';

export type CreateWorkTimeEntrySelectActivityPageProps = PropsWithoutChildren<
  Record<never, never>
>;

export const CreateWorkTimeEntrySelectActivityPage: FunctionComponent<CreateWorkTimeEntrySelectActivityPageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const workTimeEntryToCreate = useRequiredWorkTimeEntryToCreate();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { parentActivityId } = useParams<'parentActivityId'>();
    const activities = useCreateWorkTimeEntrySelectActivityPageActivities(
      workTimeEntryToCreate,
      parentActivityId ?? null
    );
    const timeTrackingActivityRepository = useEntityRepository(
      EntityName.TimeTrackingActivity
    );
    useConfigureCreateWorkTimeEntrySelectActivityPageBreadcrumbs(
      parentActivityId
    );

    const handleActivitySelected: TimeTrackingActivityToSelectListItemProps['onActivitySelected'] =
      useEventCallback((activity) => {
        const childActivities =
          timeTrackingActivityRepository.getByParentActivityId(activity.id);

        if (!childActivities.length) {
          dispatch(
            createWorkTimeEntrySlice.actions.setTimeTrackingActivityId(
              activity.id
            )
          );
          navigate(RoutePaths.createWorkTimeEntry());
        } else {
          navigate(RoutePaths.createWorkTimeEntrySelectActivity(activity.id));
        }
      });

    return (
      <PageContentContainer>
        <PageContentHeader
          title={t(
            'components.page.CreateWorkTimeEntrySelectActivityPage.title'
          )}
          iconConfig={
            iconConfigurations.prefixed.page
              .CreateWorkTimeEntrySelectActivityPage.titleIcon
          }
        ></PageContentHeader>

        {activities.map((a) => (
          <TimeTrackingActivityToSelectListItem
            key={EntityUtils.getTrackingKey(a)}
            timeTrackingActivity={a}
            onActivitySelected={handleActivitySelected}
            data-testid="CreateWorkTimeEntrySelectActivityPage.ActivityListItem"
          ></TimeTrackingActivityToSelectListItem>
        ))}
      </PageContentContainer>
    );
  });
